package diana.soleil.design.iraniancanadienbazzar.core.model

/**
 * A user↔business conversation (`conversations/{uid}_{businessId}` — deterministic id, one thread per
 * user per business; see `docs/DATA_MODEL.md`). `businessName` is snapshotted at creation so the inbox
 * renders without N business lookups. [unreadCount] is joined from the signed-in user's server-created
 * notification documents by the inbox ViewModel.
 */
data class Conversation(
    val id: String,
    val businessId: String,
    /** Display title of the thread: the business/item name, or — for a DM — the counterparty's name. */
    val businessName: String,
    /** Set for marketplace buyer↔seller threads (`item_{itemId}_{buyerUid}`); null for business chat. */
    val itemId: String? = null,
    val participantUids: List<String>,
    val lastMessage: String = "",
    val lastMessageAtMillis: Long? = null,
    val unreadCount: Int = 0,
    /** Thread kind: `business` | `item` | `dm` (direct user↔user) | `group` (member-created). */
    val type: String = TYPE_BUSINESS,
    /** Uids who have accepted the thread. Business/item threads auto-accept both sides at creation;
     * a DM starts accepted only by its creator until the recipient accepts (message-requests model). */
    val acceptedUids: List<String> = emptyList(),
    /** Who opened the thread — the DM requester / group creator. Null on legacy business/item docs. */
    val createdBy: String? = null,
    /** Display-name snapshots ({uid: name}) — group messages label senders from this. */
    val participantNames: Map<String, String> = emptyMap(),
) {
    /**
     * True when this thread is a pending message REQUEST for [selfUid]: a DM or group they were
     * invited to but have not accepted yet (and did not start themselves). Such threads live in the
     * Requests inbox.
     */
    fun isRequestFor(selfUid: String): Boolean =
        (type == TYPE_DM || type == TYPE_GROUP) && selfUid !in acceptedUids && createdBy != selfUid

    companion object {
        const val TYPE_BUSINESS = "business"
        const val TYPE_ITEM = "item"
        const val TYPE_DM = "dm"
        const val TYPE_GROUP = "group"

        /** Deterministic DM thread id — sorted uid pair so both users resolve the same document. */
        fun dmId(a: String, b: String): String = "dm_" + listOf(a, b).sorted().joinToString("_")
    }
}

/** A minimal public identity used for user discovery + DM display (`publicProfiles/{uid}`). */
data class PublicProfile(
    val uid: String,
    val displayName: String,
    val city: String? = null,
    /** Short self-description shown on the Find-your-people card (opt-in surface only). */
    val bio: String? = null,
    /** Connection-intent keys (see [PeopleIntent]); empty when not discoverable. */
    val intents: List<String> = emptyList(),
    /** Whether this person opted IN to appearing in Find your people. Default false. */
    val discoverable: Boolean = false,
    /** Separate opt-in for the people MAP. Default false; requires [discoverable] to matter. */
    val mapVisible: Boolean = false,
    /** APPROXIMATE area only — snapped to a ~2 km grid at save time; never a precise position. */
    val approxLat: Double? = null,
    val approxLng: Double? = null,
    /** How precisely [approxLat]/[approxLng] were snapped; [MapPrecision.Hidden] when not on the map. */
    val mapPrecision: MapPrecision = MapPrecision.Hidden,
    /**
     * When the member's manual map check-in ends (epoch millis). A pin is never permanent: each check-in
     * lasts [PEOPLE_MAP_CHECK_IN_MILLIS] and the member has to check in again to reappear (App Store 5.1.2).
     */
    val mapCheckInExpiresAtMillis: Long? = null,
    /** Profile photo (self-chosen; our Storage). Null = initial-avatar fallback everywhere. */
    val photoUrl: String? = null,
    /** Small self-curated gallery shown on the profile screen (max ~6). */
    val photos: List<String> = emptyList(),
    /** What they do — free text ("Pharmacist", "Grad student @ UofT"). */
    val occupation: String? = null,
    /** Where they're from originally ("Tehran", "Shiraz") — a big connector in the community. */
    val hometown: String? = null,
    /** Relationship status wire key (see [RelationshipStatus]); null = not shared. */
    val relationship: String? = null,
    /** Language codes they speak ("fa", "en", "fr") — rendered as localized labels. */
    val languages: List<String> = emptyList(),
    /** Free-text interest tags ("Hiking", "Board games") — user content, shown as chips. */
    val interests: List<String> = emptyList(),
) {
    /** True while a manual check-in is live: visible, has an area, and not yet expired. */
    fun isOnMapAt(nowMillis: Long): Boolean =
        mapVisible && approxLat != null && approxLng != null &&
            (mapCheckInExpiresAtMillis ?: 0L) > nowMillis
}

/** How long one manual people-map check-in lasts before the pin disappears: 4 hours. */
const val PEOPLE_MAP_CHECK_IN_MILLIS: Long = 4L * 60 * 60 * 1000

/** Optional relationship-status disclosure on a person's profile. Sharing is entirely opt-in. */
enum class RelationshipStatus(val wire: String) {
    Single("single"),
    InRelationship("relationship"),
    Married("married");

    companion object {
        fun fromWire(wire: String?): RelationshipStatus? = entries.firstOrNull { it.wire == wire }
    }
}

/** Connection intents for Find your people — deliberately social, NOT a dating taxonomy. */
enum class PeopleIntent(val wire: String) {
    Chatting("chatting"),
    Friends("friends"),
    MeetNewPeople("meet"),
    Networking("networking");

    companion object {
        fun fromWire(wire: String): PeopleIntent? = entries.firstOrNull { it.wire == wire }
    }
}

/** One chat message. [isMine] is resolved against the signed-in uid by the repository. */
data class Message(
    val id: String,
    val senderUid: String,
    val text: String,
    val createdAtMillis: Long? = null,
    val isMine: Boolean = false,
)

/**
 * How a member appears on the people map when they check in. There is deliberately no precise option:
 * coordinates are snapped to a grid before they are ever written, so the stored point is only an area.
 */
enum class MapPrecision(val wire: String?, val gridDegrees: Double?) {
    /** Not on the map. */
    Hidden(null, null),
    /** A city-sized area: 0.1° grid, about 11 km. */
    City("city", 0.1),
    /** A neighbourhood-sized area: 0.02° grid, about 2 km (the original map behaviour). */
    Neighbourhood("area", 0.02),
    ;

    companion object {
        /** Profiles saved before the setting existed only carry `mapVisible`; they were ~2 km areas. */
        fun fromWire(wire: String?, mapVisible: Boolean): MapPrecision = when {
            !mapVisible -> Hidden
            wire == City.wire -> City
            else -> Neighbourhood
        }
    }
}
